import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API } from '../../api/client';
import { useApi } from '../../api/useApi';
import {
  Cert, Chip, FiScoreRow, InternalDetail, KV, MatrixRow,
  SCORE_QUESTION_LABELS, STATUS_BADGE,
} from '../adminData';

const TABS = [
  { key: 'profile', label: 'Profile' },
  { key: 'technology', label: 'Technology' },
  { key: 'financials', label: 'Financials' },
  { key: 'demographics', label: 'Demographics' },
  { key: 'fi-scores', label: 'FI Scores' },
  { key: 'history', label: 'History' },
] as const;

type TabKey = typeof TABS[number]['key'];

export default function InternalApplicationDetail() {
  const { appId, tab } = useParams<{ appId: string; tab?: string }>();
  const navigate = useNavigate();
  const id = appId ?? '';
  const activeTab: TabKey = (TABS.find(t => t.key === tab)?.key) ?? 'profile';

  const { data: d, loading, error } = useApi<InternalDetail>(() => API.internalApplication(id), [id]);

  const goTab = (t: TabKey) =>
    navigate(t === 'profile' ? `/admin/applications/${id}` : `/admin/applications/${id}/${t}`);

  if (loading) return <div className="phil-loading">Loading…</div>;
  if (error || !d) return <div className="phil-error">Unable to load application. {error}</div>;

  return (
    <>
      <div className="breadcrumb" style={{ marginBottom: 14 }}>
        <a onClick={() => navigate('/admin/pipeline')} style={{ color: '#00AEEF', cursor: 'pointer' }}>Pipeline</a>
        <span style={{ color: '#ccc' }}>›</span>
        <span>{d.company}</span>
      </div>

      <div className="detail-tabs" style={{ marginBottom: 18 }}>
        {TABS.map(t => (
          <div key={t.key} className={`detail-tab${activeTab === t.key ? ' active' : ''}`} onClick={() => goTab(t.key)}>
            {t.label}
          </div>
        ))}
      </div>

      <div className="adet-header">
        <div className="adet-title-row">
          <div className="adet-title">{d.company}</div>
          <div className="adet-id">{d.id}</div>
          <span className={STATUS_BADGE[d.status] || 'phil-badge phil-badge-draft'}>{d.status}</span>
        </div>
      </div>

      {activeTab === 'profile' && <ProfileTab d={d} />}
      {activeTab === 'technology' && <TechnologyTab d={d} />}
      {activeTab === 'financials' && <FinancialsTab d={d} />}
      {activeTab === 'demographics' && <DemographicsTab d={d} />}
      {activeTab === 'fi-scores' && <FiScoresTab d={d} />}
      {activeTab === 'history' && <HistoryTab d={d} />}
    </>
  );
}

function Fields({ rows }: { rows: KV[] }) {
  return (
    <>
      {rows.map(r => (
        <div key={r.label} className="adet-field">
          <div className="k">{r.label}</div>
          <div className="v">{r.value}</div>
        </div>
      ))}
    </>
  );
}

function ProfileTab({ d }: { d: InternalDetail }) {
  return (
    <div className="adet-twocol">
      <div>
        <div className="adet-keyinfo-head">Key Info</div>
        <table className="adet-keyinfo">
          <tbody>
            {d.profile.keyInfo.map((r: KV) => (
              <tr key={r.label}>
                <td>{r.label}</td>
                <td style={{ fontWeight: r.label === 'Industry' ? 600 : 400, color: r.label === 'Website' ? '#00AEEF' : undefined }}>{r.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div>
        <div className="adet-matrix-head">Priority Matrix — Historic</div>
        <table className="adet-matrix">
          <thead><tr><th>Priority Area</th><th>FY24</th><th>FY25</th><th>FY26</th></tr></thead>
          <tbody>
            {d.profile.priorityMatrix.map((m: MatrixRow) => (
              <tr key={m.area}>
                <td>{m.area}</td>
                {[m.fy24, m.fy25, m.fy26].map((c, i) => (
                  <td key={i}>{c === 'TBD' ? <span className="tbd">TBD</span> : c === 'X' ? <span className="x">X</span> : ''}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TechnologyTab({ d }: { d: InternalDetail }) {
  return (
    <div className="adet-twocol">
      <div>
        <div className="adet-group-cyan">Product Overview</div>
        <Fields rows={d.technology.productOverview} />
      </div>
      <div>
        <div className="adet-group-navy">Technical Readiness</div>
        <Fields rows={d.technology.technicalReadiness} />
        <div className="adet-field">
          <div className="k">Client Exploration Options</div>
          <div className="adet-chips">
            {d.technology.explorationOptions.map(o => <span key={o} className="adet-chip">{o}</span>)}
          </div>
        </div>
        <div className="adet-chips" style={{ marginTop: 4 }}>
          {d.technology.certifications.map((c: Cert) => (
            <span key={c.label} className={c.ok ? 'adet-cert-ok' : 'adet-cert-no'}>{c.ok ? '✓' : '✗'} {c.label}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

function FinancialsTab({ d }: { d: InternalDetail }) {
  return (
    <div className="adet-twocol">
      <div>
        <div className="adet-group-cyan">Funding &amp; Stage</div>
        <Fields rows={d.financials.fundingStage} />
      </div>
      <div>
        <div className="adet-group-navy">Revenue &amp; Growth</div>
        <Fields rows={d.financials.revenueGrowth} />
      </div>
    </div>
  );
}

function DemographicsTab({ d }: { d: InternalDetail }) {
  return (
    <div className="adet-twocol">
      <div>
        <div className="adet-group-cyan">CEO Identity</div>
        <div className="adet-field">
          <div className="k">Race / Ethnicity</div>
          <div className="adet-chips">
            {d.demographics.race.map((c: Chip) => (
              <span key={c.label} className={c.selected ? 'adet-chip-on' : 'adet-chip-off'}>{c.label}</span>
            ))}
          </div>
        </div>
        <div className="adet-field">
          <div className="k">Gender Identity</div>
          <div className="v">{d.demographics.gender}</div>
        </div>
        <div className="adet-field">
          <div className="k">CEO Identifies As</div>
          <div className="adet-chips">
            {d.demographics.identifiesAs.map((c: Chip) => (
              <span key={c.label} className={c.selected ? 'adet-chip-on' : 'adet-chip-off'}>{c.label}</span>
            ))}
          </div>
        </div>
      </div>
      <div>
        <div className="adet-group-navy">ESG Commitments</div>
        {d.demographics.esg.map((e: Cert) => (
          <div key={e.label} className={`adet-esg-row${e.ok ? '' : ' off'}`}>
            <span className={e.ok ? 'yes' : 'no'}>{e.ok ? '✓' : '✗'}</span>{e.label}
          </div>
        ))}
        <div className="adet-note-box">
          <div className="k">Internal Notes</div>
          <div className="v">{d.demographics.internalNote}</div>
        </div>
      </div>
    </div>
  );
}

function scoreChip(v: number | null) {
  if (v === null || v === 0) return <span className="score-chip score-skip">—</span>;
  return <span className={`score-chip score-${v}`}>{v}</span>;
}

function FiScoresTab({ d }: { d: InternalDetail }) {
  const s = d.fiScores;
  const colCount = SCORE_QUESTION_LABELS.length + 3;
  const [openNotes, setOpenNotes] = React.useState<Record<number, boolean>>({});
  const toggleNote = (i: number) =>
    setOpenNotes(prev => ({ ...prev, [i]: !prev[i] }));
  return (
    <>
      <div className="adet-score-summary">
        <div className="adet-score-overall">
          <div className="num">{s.overallAvg.toFixed(1)}</div>
          <div className="lbl">Overall Avg</div>
        </div>
        <div className="adet-score-byq">
          <div className="hd">Score by Question (avg across all FIs)</div>
          <div className="row">
            {s.byQuestion.map(b => (
              <div key={b.q} className="cell">
                <div className="ql">{b.q}</div>
                {scoreChip(b.avg)}
              </div>
            ))}
          </div>
        </div>
        <div className="adet-score-status">
          <div className="hd">Evaluation Status</div>
          <div className="line"><span className="big" style={{ color: '#28C840' }}>{s.scored}</span> FIs scored</div>
          <div className="line"><span className="big" style={{ color: '#F5A623' }}>{s.pending}</span> pending</div>
        </div>
      </div>

      <div className="phil-card" style={{ overflowX: 'auto' }}>
        <table className="adet-scores-table">
          <thead>
            <tr>
              <th className="left">FI</th>
              <th className="left">Evaluator</th>
              {SCORE_QUESTION_LABELS.map(q => <th key={q}>{q}</th>)}
              <th className="avgcol">FI Avg</th>
            </tr>
          </thead>
          <tbody>
            {s.rows.map((r: FiScoreRow, ri: number) => {
              const hasNote = !!(r.notes && r.notes.trim());
              const open = !!openNotes[ri];
              return (
                <React.Fragment key={r.fi}>
                  <tr
                    className={hasNote ? 'fi-score-row has-note' : 'fi-score-row'}
                    onClick={hasNote ? () => toggleNote(ri) : undefined}
                    style={hasNote ? { cursor: 'pointer' } : undefined}
                  >
                    <td className="left" style={{ fontWeight: 700 }}>
                      {r.fi}
                      {hasNote && (
                        <span
                          className="fi-note-dot"
                          title="Evaluator left a note — click to view"
                          aria-label="Has evaluator note"
                        >
                          {open ? '▾' : '▸'} 📝
                        </span>
                      )}
                    </td>
                    <td className="left" style={{ color: '#666' }}>{r.evaluator}</td>
                    {r.scores.map((v, i) => <td key={i}>{scoreChip(v)}</td>)}
                    <td className="avgcol">{scoreChip(r.fiAvgRounded)} <span style={{ fontSize: 11, color: '#888' }}>{r.fiAvgExact.toFixed(1)}</span></td>
                  </tr>
                  {hasNote && open && (
                    <tr className="fi-note-row">
                      <td colSpan={colCount}>
                        <div className="fi-note-body">
                          <span className="fi-note-label">{r.fi} note</span>
                          {r.notes}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            <tr className="crossrow">
              <td className="left" colSpan={2} style={{ color: '#1A2744' }}>Cross-FI Average</td>
              {s.crossAvg.map((v, i) => <td key={i}>{scoreChip(v)}</td>)}
              <td className="avgcol">{scoreChip(s.crossAvgRounded)} <span style={{ fontSize: 11, color: '#888' }}>{s.crossAvgExact.toFixed(1)}</span></td>
            </tr>
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: 10, fontSize: 11, color: '#aaa', fontStyle: 'italic' }}>Score guide — {s.guide}</div>
    </>
  );
}

function HistoryTab({ d }: { d: InternalDetail }) {
  const outcomeClass = (o: string) =>
    o === 'Selected' ? 'outcome-selected' : o === 'Not Selected' ? 'outcome-not' : 'outcome-prog';
  return (
    <div className="adet-twocol">
      <div>
        <div className="adet-group-cyan">Application History</div>
        <table className="phil-table" style={{ fontSize: 12 }}>
          <thead><tr><th>Cycle</th><th>Status</th><th>Overall Score</th><th>Outcome</th></tr></thead>
          <tbody>
            {d.history.cycles.map(c => (
              <tr key={c.cycle}>
                <td style={{ fontWeight: 700 }}>{c.cycle}</td>
                <td><span className="phil-badge phil-badge-complete">{c.status}</span></td>
                <td>{scoreChip(c.scoreRounded)} <span style={{ fontSize: 11, color: '#888' }}>{c.scoreExact.toFixed(1)}</span></td>
                <td className={outcomeClass(c.outcome)}>{c.outcome}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {d.history.mentorship.length > 0 && (
          <>
            <div className="adet-group-navy" style={{ marginTop: 20 }}>Mentorship Record</div>
            <table className="phil-table" style={{ fontSize: 12 }}>
              <thead><tr><th>FI Mentor</th><th>Sessions</th><th>Focus Area</th></tr></thead>
              <tbody>
                {d.history.mentorship.map(m => (
                  <tr key={m.mentor}><td>{m.mentor}</td><td>{m.sessions}</td><td>{m.focus}</td></tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
      <div>
        <div className="adet-group-navy">Activity Log</div>
        {d.history.activityLog.map((a, i) => (
          <div key={i} className="adet-activity">
            <div className="date">{a.date}</div>
            <div>
              <div className="title">{a.title}</div>
              <div className="detail">{a.detail}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
